// Shared quick-action presets for the agent chat (full page + dock).
// `prompt` is what gets sent to the agent — phrased to kick off the right
// tool flow. `label` is what the client sees on the chip.
//
// QUICK_QUESTIONS surface as "Ask a question" chips; QUICK_REQUESTS surface
// as "Submit a change request" chips. Creative is intentionally omitted —
// creative is 100% designed in-house and clients do not concept, request,
// or review creative through the agent.
export const QUICK_QUESTIONS = [
  {
    label: "How's my performance?",
    prompt:
      "Give me a summary of my campaign performance this month — spend, leads, CPL, and how I'm pacing against budget.",
  },
  {
    label: "How's my budget pacing?",
    prompt:
      "How is my budget pacing this month? Show me total spent, remaining, and which segments are over- or under-pacing.",
  },
  {
    label: 'Compare to last month',
    prompt:
      'Compare my performance this month vs last month — spend, leads, CPL, and booked appointments. Highlight the biggest changes.',
  },
  {
    label: 'Show me my leads',
    prompt:
      'Show me a summary of my leads this month — total volume, quality breakdown, top zips, and how many self-booked.',
  },
];

export const QUICK_REQUESTS = [
  {
    label: 'Change my budget',
    prompt:
      "I'd like to change my budget. Ask me what you need (amount, segment, when to take effect) so you can submit the request.",
  },
  {
    label: 'Update my ZIP coverage',
    prompt:
      "I'd like to add or remove ZIP codes from my coverage. Show me current coverage first, then help me put in the request.",
  },
  {
    label: 'Pause / restart campaigns',
    prompt:
      'I need to pause or restart a campaign. Ask me what you need (which scope, when to take effect, why) and submit the request.',
  },
];

// Page-aware starters. Chips that name what's on screen get used; the
// generic four above started 18 of 99 client sessions. First matching
// pathname wins; anything else falls back to QUICK_QUESTIONS.
const PAGE_QUESTIONS = [
  {
    match: /^\/clients\/[^/]+\/dashboard/,
    questions: [
      { label: 'What should I look at first?', prompt: "Looking at my dashboard, what's the one thing I should pay attention to right now, and why?" },
      { label: 'Which market is my best?', prompt: 'Rank my territories by cost of marketing over the last 90 days and tell me which one to lean into.' },
      { label: 'How do I compare to peers?', prompt: 'Benchmark my funnel against other clients in my vertical and tell me my single biggest lever.' },
      { label: 'Am I on pace this month?', prompt: 'How is my budget pacing this month, and where will I land by month end?' },
    ],
  },
  {
    match: /budget-center/,
    questions: [
      { label: 'Why is a market over-pacing?', prompt: 'Which of my segments are over- or under-pacing this month, and is that a problem given their cost of marketing?' },
      { label: 'Where should I add budget?', prompt: 'Which segments should I add budget to next month, based on cost of marketing and remaining headroom?' },
      { label: 'Turn a market off', prompt: 'I want to stop spending in one of my markets for the rest of the month. Ask me which one and file the turn-off.' },
      { label: 'What will I land at?', prompt: "What's my projected month-end spend against my approved budget, overall and by segment?" },
    ],
  },
  {
    match: /zip-coverage/,
    questions: [
      { label: 'Which ZIPs perform best?', prompt: 'Which of my territories and ZIPs are producing the best cost of marketing over the last 90 days?' },
      { label: 'What does "filtered" mean?', prompt: 'What do filtered and non-targetable mean on my ZIP coverage, and should I change anything?' },
      { label: 'Add ZIPs to a territory', prompt: "I'd like to add ZIP codes to one of my territories. Show me current coverage first, then help me put in the request." },
      { label: 'Retire a territory', prompt: 'I want to stop servicing one of my territories entirely. Walk me through what that means and file it.' },
    ],
  },
  {
    match: /invoices/,
    questions: [
      { label: 'What do I owe?', prompt: 'What invoices are open on my account, what is due when, and how do I pay them?' },
      { label: 'Reconcile last month', prompt: "Break down last month's invoice: ad spend by market plus the management fee, so I can reconcile it." },
    ],
  },
  {
    match: /lead-delivery|data-integrations|backend-data|webhook/,
    questions: [
      { label: 'Are my leads posting?', prompt: 'Are my leads posting to my CRM successfully? Show me recent delivery results and any failures.' },
      { label: 'Send a test lead', prompt: 'How do I send a test lead to my CRM from the Lead Delivery page?' },
      { label: 'Is my sales data flowing?', prompt: 'Is my sales outcome data coming through? Which funnel stages are populated and which are missing?' },
    ],
  },
  {
    match: /projections/,
    questions: [
      { label: 'How real is this forecast?', prompt: 'How do my actual results compare to the projections on this page, and which numbers are planning defaults rather than benchmarks?' },
      { label: 'Am I on pace this month?', prompt: 'How is my budget pacing this month, and where will I land by month end?' },
    ],
  },
  {
    match: /ad-performance|creative/,
    questions: [
      { label: 'Which ads are working?', prompt: 'Which of my ads have the best cost of marketing over the last 90 days, and which are dragging?' },
      { label: 'Lead quality this week', prompt: 'Walk me through the quality of leads from the last 7 days.' },
    ],
  },
  {
    match: /\/leads/,
    questions: [
      { label: 'Lead quality this week', prompt: 'Walk me through the quality of leads from the last 7 days.' },
      { label: 'Self-booked leads', prompt: 'How many of my leads self-booked this month, and how does that compare to my vertical?' },
      { label: 'Out-of-area leads', prompt: 'How many of my leads this month were out of area, and what does that mean for me?' },
    ],
  },
];

export function getQuickQuestions(pathname = '') {
  const hit = PAGE_QUESTIONS.find((page) => page.match.test(pathname || ''));
  return hit ? hit.questions : QUICK_QUESTIONS;
}
